import { COMMON_TIMEZONES } from '../constants';
import type {
  CompanyFieldKey,
  CompanyFieldSettingDefinition,
} from './registry.types';

const SETTINGS_ROUTE = '/settings';
const MANAGEMENT_ROUTE = '/management';
/** The internal-tools company configuration table (`internal/frontend`). */
const INTERNAL_ROUTE = '/company-configurations';

const GENERAL_PAGE = { route: SETTINGS_ROUTE, tab: 'general' } as const;
const BILLING_PAGE = { route: MANAGEMENT_ROUTE, tab: 'billing' } as const;
const INTERNAL_PAGE = { route: INTERNAL_ROUTE } as const;

const GENERAL_WRITE = { resource: 'SETTINGS_GENERAL', level: 'WRITE' } as const;
const BILLING_WRITE = { resource: 'COMPANY_BILLING', level: 'WRITE' } as const;
const MASTER = { resource: 'MASTER' } as const;

/**
 * Settings-page entries that persist as `Company` columns rather than in
 * `Company.configs`. Search and the settings draft read them alongside
 * {@link COMPANY_SETTINGS}; saving routes them by `writePath` rather than
 * through `updateCompanyConfiguration`. Most take `updateCompany`, but read
 * each entry's `writePath` rather than assuming it — `invoiceEmail` has its
 * own writer, and collapsing it back into `updateCompany` strands the
 * company's SendGrid sender.
 */
export const COMPANY_FIELD_SETTINGS = {
  blockSendingZeroDollarInvoices: {
    key: 'blockSendingZeroDollarInvoices',
    writePath: 'company',
    label: 'Block sending $0 invoices',
    description: 'An invoice totalling zero cannot be emailed to a customer.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice', 'email'],
    control: { type: 'switch' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  customDunningReportFooter: {
    key: 'customDunningReportFooter',
    writePath: 'company',
    label: 'Dunning report footer',
    description: 'Text printed at the bottom of the dunning report.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice', 'customer'],
    control: { type: 'text', multiline: true },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  customDunningReportHeader: {
    key: 'customDunningReportHeader',
    writePath: 'company',
    label: 'Dunning report header',
    description: 'Text printed at the top of the dunning report.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice', 'customer'],
    control: { type: 'text', multiline: true },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  defaultInvoiceEmailSubject: {
    key: 'defaultInvoiceEmailSubject',
    writePath: 'company',
    label: 'Invoice email subject',
    description:
      'Subject line for outgoing invoice emails, accepting the company, invoice and customer variables.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['email', 'customer'],
    control: { type: 'text' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  defaultMeasurementUnits: {
    key: 'defaultMeasurementUnits',
    writePath: 'company',
    label: 'Default measurement units',
    description: 'Units dimensions are entered and shown in.',
    page: 'billing',
    group: 'Units',
    surfaces: ['orders', 'invoice'],
    control: {
      type: 'select',
      options: [
        { value: 'INCHES', label: 'Inches' },
        { value: 'CENTIMETERS', label: 'Centimeters' },
      ],
    },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  defaultNonCompanyDriverSettlementMode: {
    key: 'defaultNonCompanyDriverSettlementMode',
    writePath: 'company',
    label: 'Non-company driver settlement basis',
    description:
      'Whether a non-company driver earns from rates or from a percentage of the shipment charges.',
    page: 'billing',
    group: 'Driver settlement',
    surfaces: ['invoice'],
    control: {
      type: 'select',
      options: [
        { value: 'INDEPENDENT', label: 'Rates' },
        { value: 'DEPENDENT', label: 'Percentage of shipments' },
      ],
    },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  defaultWeightUnits: {
    key: 'defaultWeightUnits',
    writePath: 'company',
    label: 'Default weight units',
    description: 'Units weights are entered and shown in.',
    page: 'billing',
    group: 'Units',
    surfaces: ['orders', 'invoice'],
    control: {
      type: 'select',
      options: [
        { value: 'POUNDS', label: 'Pounds' },
        { value: 'KILOGRAMS', label: 'Kilograms' },
      ],
    },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  invoiceEmail: {
    key: 'invoiceEmail',
    writePath: 'invoice-email',
    label: 'Invoice email',
    description:
      'Address outgoing invoice emails are sent from. The billing control is disabled while the SendGrid integration is down, and its tooltip sends users to support.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['email', 'customer'],
    control: { type: 'text' },
    permission: BILLING_WRITE,
    exposedToUser: false,
    location: BILLING_PAGE,
  },
  phone: {
    key: 'phone',
    writePath: 'company',
    label: 'Company phone number',
    description:
      'The company contact number carried on documents and notifications.',
    page: 'internal',
    group: 'Company',
    surfaces: ['customer'],
    control: { type: 'text' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  qbInvoiceNumberStart: {
    key: 'qbInvoiceNumberStart',
    writePath: 'company',
    label: 'QuickBooks invoice number start',
    description:
      'The first number invoices exported to QuickBooks are numbered from.',
    page: 'internal',
    group: 'Company',
    surfaces: ['invoice'],
    control: { type: 'number', min: 1, integer: true },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  timeZone: {
    key: 'timeZone',
    writePath: 'company',
    label: 'Timezone',
    description:
      'The IANA time zone every date on the web app and on documents is read in.',
    page: 'general',
    group: 'Company',
    surfaces: ['dispatch-board', 'orders', 'invoice'],
    control: {
      type: 'select',
      options: COMMON_TIMEZONES.map((timeZone) => ({
        value: timeZone,
        label: timeZone,
      })),
    },
    permission: GENERAL_WRITE,
    exposedToUser: true,
    location: GENERAL_PAGE,
  },
  useSageBusinessWorks: {
    key: 'useSageBusinessWorks',
    writePath: 'company',
    label: 'Use Sage BusinessWorks',
    description: 'Formats accounting exports for Sage BusinessWorks.',
    page: 'billing',
    group: 'Accounting',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
} satisfies Record<CompanyFieldKey, CompanyFieldSettingDefinition>;
